import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Camera, X, Star, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { api, imageUrl, formatApiErrorDetail } from "@/lib/api";
import { MediaPreview } from "@/components/MediaPreview";

export const MAX_IMAGES = 10;

export const uploadImage = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data.path;
};

// Up to 10 photos; first one is the main photo; arrows reorder and ★ promotes (mobile ImagesPicker).
export function ImagesPicker({ images, onChange, lang, onBusy }) {
  const gallery = useRef(null);
  const camera = useRef(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { onBusy?.(busy); }, [busy]); // eslint-disable-line react-hooks/exhaustive-deps
  const [view, setView] = useState(null);
  const L = (ar, en) => (lang === "ar" ? ar : en);
  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, MAX_IMAGES - images.length);
    e.target.value = "";
    if (!files.length) return;
    setBusy(true);
    let next = [...images];
    try {
      for (const f of files) { next = [...next, await uploadImage(f)]; onChange(next); }
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setBusy(false);
  };
  const move = (i, d) => { const n = [...images]; [n[i], n[i + d]] = [n[i + d], n[i]]; onChange(n); };
  const main = (i) => onChange([images[i], ...images.filter((_, j) => j !== i)]);
  return (
    <div>
      <p className="text-xs text-slate-500 mb-3">{L("الصورة الأولى هي الرئيسية التي تظهر في القوائم. استخدم الأسهم لترتيب الصور أو ★ لجعل أي صورة رئيسية. اضغط على أي صورة لمعاينتها مكبّرة.", "The first photo is the main one shown in listings. Use the arrows to reorder or ★ to make a photo the main one. Tap any photo to preview it full size.")}</p>
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
        {images.map((p, i) => (
          <div key={p} className="relative aspect-square rounded-xl overflow-hidden border border-[#E5E5E0] bg-slate-100" data-testid={`post-image-${i}`}>
            <img src={imageUrl(p)} alt="" onClick={() => setView(i)} className="w-full h-full object-cover cursor-zoom-in" data-testid={`preview-image-${i}`} />
            {i === 0 && <span className="absolute top-1 end-1 bg-[#D4AF37] text-white text-[10px] font-bold px-2 py-0.5 rounded-full">{L("رئيسية", "Main")}</span>}
            <button type="button" onClick={() => onChange(images.filter((x) => x !== p))} className="absolute top-1 start-1 bg-black/60 text-white rounded-full p-1" data-testid={`remove-image-${i}`}><X className="w-3 h-3" /></button>
            <div className="absolute bottom-1 inset-x-1 flex justify-between">
              <button type="button" disabled={i === 0} onClick={() => move(i, -1)} className="bg-black/50 text-white rounded-full p-1 disabled:opacity-30" data-testid={`move-prev-${i}`}><ChevronRight className="w-3 h-3 rtl:rotate-0 ltr:rotate-180" /></button>
              {i > 0 && <button type="button" onClick={() => main(i)} title={L("اجعلها الصورة الرئيسية", "Set as main photo")} className="bg-black/50 text-[#D4AF37] rounded-full p-1" data-testid={`set-main-${i}`}><Star className="w-3 h-3" /></button>}
              <button type="button" disabled={i === images.length - 1} onClick={() => move(i, 1)} className="bg-black/50 text-white rounded-full p-1 disabled:opacity-30" data-testid={`move-next-${i}`}><ChevronLeft className="w-3 h-3 rtl:rotate-0 ltr:rotate-180" /></button>
            </div>
          </div>
        ))}
        {images.length < MAX_IMAGES && (
          <>
            <button type="button" onClick={() => gallery.current?.click()} className="aspect-square rounded-xl border-2 border-dashed border-slate-300 flex flex-col items-center justify-center gap-1 text-slate-500 hover:border-[#064E3B] hover:text-[#064E3B]" data-testid="images-from-gallery">
              {busy ? <Loader2 className="w-6 h-6 animate-spin" /> : <ImagePlus className="w-6 h-6" />}<span className="text-xs font-semibold">{L("من المعرض", "From gallery")}</span>
            </button>
            <button type="button" onClick={() => camera.current?.click()} className="aspect-square rounded-xl border-2 border-dashed border-slate-300 flex flex-col items-center justify-center gap-1 text-slate-500 hover:border-[#064E3B] hover:text-[#064E3B]" data-testid="images-take-photo">
              <Camera className="w-6 h-6" /><span className="text-xs font-semibold">{L("التقاط صورة", "Take photo")}</span>
            </button>
          </>
        )}
      </div>
      <input ref={gallery} type="file" accept="image/*" multiple className="hidden" onChange={onFiles} data-testid="upload-image-input" />
      <MediaPreview items={images} index={view} onIndex={setView} onClose={() => setView(null)} title={L("معاينة الصور", "Photo preview")} lang={lang} testid="images-preview" />
      <input ref={camera} type="file" accept="image/*" capture="environment" className="hidden" onChange={onFiles} />
    </div>
  );
}
